import React from 'react';
import { Minus, X, MessageSquare } from 'lucide-react';
import { ADMIN_PALETTE, STATUS_PALETTE } from './adminDesignSystem';
import { Card } from './AdminPanelPrimitives';

export const AdminFloatingSupportBubble = ({
  isMobileView,
  floatingSupportMinimized,
  setFloatingSupportMinimized,
  setFloatingSupportVisible,
  selectedSupportThread,
  userId,
  updateSupportStatus,
  supportDraft,
  setSupportDraft,
  sendSupportReply,
  scrollAnchorRef,
}) => (
  <div
    data-testid="admin-floating-support"
    style={{
      position: 'fixed',
      right: 22,
      bottom: isMobileView ? 80 : 96,
      width: floatingSupportMinimized ? 260 : (isMobileView ? 'calc(100vw - 32px)' : 380),
      zIndex: 80,
      animation: 'admin-support-bubble-in 220ms ease-out',
    }}
  >
    <Card style={{ padding: 0, overflow: 'hidden', boxShadow: '0 20px 50px rgba(15,17,21,0.22)', border: '1px solid rgba(15,17,21,0.08)' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, padding: '12px 14px',
        background: ADMIN_PALETTE.ink, color: '#FFFFFF',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 13, flexShrink: 0,
          }}>
            {(selectedSupportThread.user_name || '?').trim().charAt(0).toUpperCase()}
          </span>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selectedSupportThread.user_name}
            </p>
            {!floatingSupportMinimized && (
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedSupportThread.user_email}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            onClick={() => setFloatingSupportMinimized((c) => !c)}
            data-testid="admin-floating-support-minimize-button"
            aria-label={floatingSupportMinimized ? 'Expand' : 'Minimize'}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 8, color: '#FFFFFF', display: 'inline-flex' }}
          >
            <Minus size={14} />
          </button>
          <button
            type="button"
            onClick={() => setFloatingSupportVisible(false)}
            data-testid="admin-floating-support-close-button"
            aria-label="Close"
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 8, color: '#FFFFFF', display: 'inline-flex' }}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!floatingSupportMinimized && (
        <div style={{ padding: 14 }}>
          {/* Status toggles */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => updateSupportStatus(selectedSupportThread.id, 'open')}
              data-testid="admin-floating-support-status-open"
              style={{
                flex: 1, padding: '7px 10px', borderRadius: 10,
                border: `1px solid ${selectedSupportThread.status !== 'solved' ? STATUS_PALETTE.urgent.solid : '#E5E7EB'}`,
                background: selectedSupportThread.status !== 'solved' ? STATUS_PALETTE.urgent.soft : '#FFFFFF',
                color: selectedSupportThread.status !== 'solved' ? STATUS_PALETTE.urgent.text : '#6B7280',
                cursor: 'pointer', fontWeight: 700, fontSize: 12,
              }}
            >
              ● Open
            </button>
            <button
              onClick={() => updateSupportStatus(selectedSupportThread.id, 'solved')}
              data-testid="admin-floating-support-status-solved"
              style={{
                flex: 1, padding: '7px 10px', borderRadius: 10,
                border: `1px solid ${selectedSupportThread.status === 'solved' ? STATUS_PALETTE.active.solid : '#E5E7EB'}`,
                background: selectedSupportThread.status === 'solved' ? STATUS_PALETTE.active.soft : '#FFFFFF',
                color: selectedSupportThread.status === 'solved' ? STATUS_PALETTE.active.text : '#6B7280',
                cursor: 'pointer', fontWeight: 700, fontSize: 12,
              }}
            >
              ✓ Solved
            </button>
          </div>

          {/* Messages */}
          <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingRight: 2 }}>
            {(selectedSupportThread.messages || []).map((message) => {
              const isAdmin = message.sender_id === userId;
              return (
                <div
                  key={message.id}
                  style={{
                    alignSelf: isAdmin ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    background: isAdmin ? ADMIN_PALETTE.ink : '#F1F2F5',
                    color: isAdmin ? '#FFF' : '#0F1115',
                    borderRadius: isAdmin ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    padding: '8px 12px',
                    boxShadow: '0 1px 2px rgba(15,17,21,0.05)',
                  }}
                >
                  <p style={{ margin: 0, fontSize: 10, opacity: 0.7, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                    {isAdmin ? 'You' : selectedSupportThread.user_name}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 13, lineHeight: 1.4 }}>{message.content}</p>
                </div>
              );
            })}
            <div ref={scrollAnchorRef} data-testid="admin-floating-support-scroll-anchor" />
          </div>

          {/* Reply input */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
            border: '1px solid #E5E7EB', borderRadius: 12,
            padding: '8px 10px', background: '#F6F7F9',
          }}>
            <input
              value={supportDraft}
              onChange={(e) => setSupportDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendSupportReply(); }}
              data-testid="admin-floating-support-reply-input"
              placeholder="Reply to the user…"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#0F1115' }}
            />
            <button
              onClick={sendSupportReply}
              data-testid="admin-floating-support-reply-send"
              disabled={!supportDraft.trim()}
              aria-label="Send reply"
              style={{
                background: supportDraft.trim() ? ADMIN_PALETTE.ink : '#D1D5DB',
                border: 'none',
                cursor: supportDraft.trim() ? 'pointer' : 'not-allowed',
                padding: '6px 8px', borderRadius: 8, color: '#FFFFFF',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <MessageSquare size={14} />
            </button>
          </div>
        </div>
      )}
    </Card>
  </div>
);
